import {
	Box,
	Button,
	Divider,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	InputLabel,
	Radio,
	RadioGroup,
	TextField,
	Typography
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import Lottie from "react-lottie";

import "../ots.css";
import { useAppDispatch } from "../../../redux";
import {
	useLazyGetSyncStatusQuery,
	useRestartBackgroundJobsMutation,
	useStartOTSSyncMutation,
	useUpdateConfigMutation,
	useUpdateSyncControllerMutation
} from "../../../redux/reducers/ots.reducer";
import { BARRIER_CONFIG_E, CONFIG_MODE_E, IP_V4_REGEX, RPI_DEVICE_NAME } from "../../../utils/constants";
import { showFeedbackNotification } from "../../../redux/reducers/notifications.reducer";
import SyncLoaderJSON from "../../../assets/loaders/sync-loader-lottie.json";
import { ReactComponent as SyncFailedImage } from "../../../assets/images/OTS/sync-failed.svg";
import { ReactComponent as SyncSuccessImage } from "../../../assets/images/OTS/sync-success.svg";
import {
	useCheckOtsStatusQuery,
	useCreateOwnerTypeMutation,
	useLazyCreateVisitTypeQuery
} from "../../../redux/reducers/settings.reducer";
import ConfirmationDialog from "../../../common/ConfirmationDialog";
import {
	ICreateOwnerTypeRequestData,
	ICreateVisitTypeRequestData,
	IUpdateConfigRequestData
} from "../../../interfaces";

interface IDeviceRegistrationInputs {
	serial_number: string;
	local_ip: string;
	global_ip: string;
	device_type: string;
	connect_ip: string;
}

const DEFAULT_OWNER_TYPES: ICreateOwnerTypeRequestData[] = [
	{ type_of: "Member", has_expiry: false },
	{ type_of: "Tenant", has_expiry: true }
];

const DEFAULT_VISIT_TYPES: ICreateVisitTypeRequestData[] = [
	{ type_of: "Resident Visit" },
	{ type_of: "Delivery or Courier" },
	{ type_of: "Maintenance or Service" },
	{ type_of: "Emergency" },
	{ type_of: "Social or Event" },
	{ type_of: "Real Estate" },
	{ type_of: "Personal Services" },
	{ type_of: "Club House or Facilities" },
	{ type_of: "Others" }
];

const DeviceRegistration: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const [statusPollingEnabled, setStatusPollingEnabled] = useState<boolean>(false);
	const [showManualRegistrationWarning, setShowManualRegistrationWarning] = useState<boolean>(false);
	const [manualRegistrationLoader, setManualRegistrationLoader] = useState<boolean>(false);

	// APIS
	const { data: siteConfig } = useCheckOtsStatusQuery(null);
	const [createOwnerType] = useCreateOwnerTypeMutation();
	const [createVisitType] = useLazyCreateVisitTypeQuery();

	const [updateConfig, { isLoading: updateConfigLoading, reset: resetUpdateConfigState }] = useUpdateConfigMutation();

	// UPDATE SYNC CONTROLLER
	const [updateSyncController, { isLoading: updateSyncControllerLoading, reset: resetUpdateSyncControllerState }] =
		useUpdateSyncControllerMutation();

	const [
		startOTSSync,
		{
			isLoading: startSyncLoading,
			error: startSyncError,
			isSuccess: startSyncSuccess,
			data: startSyncResponse
			// reset: resetStartOTSSync
		}
	] = useStartOTSSyncMutation();

	const [
		startGetSyncStatus,
		{
			isLoading: getSyncStatusLoading,
			isFetching: getSyncStatusFetching,
			error: getSyncStatusError,
			isSuccess: getSyncStatusSuccess,
			data: getSyncStatusResponse
		}
	] = useLazyGetSyncStatusQuery({ pollingInterval: statusPollingEnabled ? 5 * 1000 : 0 });

	// RESTART BACKGROUND JOBS
	const [restartBackgroundJobs, { isLoading: restartBackgroundJobsLoading }] = useRestartBackgroundJobsMutation();

	const {
		reset,
		watch,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<IDeviceRegistrationInputs>();

	const status = useMemo<"loading" | "error" | "success" | "form">(() => {
		if (startSyncSuccess) {
			if (startSyncResponse && "subscription" in startSyncResponse && !startSyncResponse.subscription) return "error";

			if (getSyncStatusResponse) {
				if (getSyncStatusResponse.is_sync_in_progress && !getSyncStatusResponse.is_synced) return "loading";
				if (getSyncStatusResponse.is_failed) return "error";
				if (getSyncStatusResponse.is_synced) return "success";
			} else {
				// getSyncStatusError is also added here, as the API will be called again with polling
				if (getSyncStatusLoading || getSyncStatusFetching || getSyncStatusError) return "loading";
			}
		} else {
			if (startSyncLoading) return "loading";
			if (startSyncError) return "error";
		}

		return "form";
	}, [
		getSyncStatusError,
		getSyncStatusFetching,
		getSyncStatusLoading,
		getSyncStatusResponse,
		startSyncError,
		startSyncLoading,
		startSyncResponse,
		startSyncSuccess
	]);

	const serialNumber = watch("serial_number");
	const deviceType = watch("device_type");

	// function handleOpenManualRegistrationWarningDialog() {
	// 	setShowManualRegistrationWarning(true);
	// }

	function handleCloseManualRegistrationWarningDialog() {
		setShowManualRegistrationWarning(false);
	}

	function handleProceedManualRegistration() {
		setStatusPollingEnabled(false);
		handleCloseManualRegistrationWarningDialog();

		if (location.state && typeof location.state.mode === "number") {
			if (location.state.mode === CONFIG_MODE_E.MY_GATE) {
				navigate("/ots/property-registration", { state: { ...location.state } });
				return;
			}
		}

		setManualRegistrationLoader(true);

		const promiseArray: Promise<void>[] = [];

		for (const ownerTypeDetails of DEFAULT_OWNER_TYPES) {
			promiseArray.push(
				new Promise((resolve, reject) => {
					createOwnerType(ownerTypeDetails)
						.unwrap()
						.then(() => resolve())
						.catch((error) => {
							if (
								error &&
								error.data &&
								(error.data === "continue" ||
									(error.data.type_of &&
										Array.isArray(error.data.type_of) &&
										error.data.type_of[0] === "owner type with this type of already exists."))
							) {
								resolve();
							} else {
								reject(error);
							}
						});
				})
			);
		}

		for (const visitTypeDetails of DEFAULT_VISIT_TYPES) {
			promiseArray.push(
				new Promise((resolve, reject) => {
					createVisitType(visitTypeDetails)
						.unwrap()
						.then(() => resolve())
						.catch((error) => {
							if (
								error &&
								error.data &&
								(error.data === "continue" ||
									(error.data.type_of &&
										Array.isArray(error.data.type_of) &&
										error.data.type_of[0] === "visit type with this type of already exists."))
							) {
								resolve();
							} else {
								reject(error);
							}
						});
				})
			);
		}

		Promise.all(promiseArray)
			.then(() => navigate("/ots/property-registration", { state: { ...location.state } }))
			.catch((error) => console.error("FAILED TO CREATE OWNER TYPE", error))
			.finally(() => setManualRegistrationLoader(false));
	}

	const handleStartOTSSync = useCallback(() => {
		startOTSSync({});
		dispatch(
			showFeedbackNotification({
				message: "Sync process started",
				severity: "success"
			})
		);
	}, [dispatch, startOTSSync]);

	function handleSubmitForm(data: IDeviceRegistrationInputs) {
		const requestData: IUpdateConfigRequestData = {
			serial_num: data.serial_number,
			local_ip: data.local_ip,
			global_ip: data.global_ip,
			// mode: mode as CONFIG_MODE_E,
			barrier_config: Number(data.device_type)
		};

		if (location.state && typeof location.state.mode === "number") {
			switch (location.state.mode) {
				case CONFIG_MODE_E.GATE_GUARD_ON_PREMISE:
					const cloudUrl = data.connect_ip ?? "https://connect.stellarview.ai";

					requestData.mode = CONFIG_MODE_E.GATE_GUARD;
					requestData.enable_gg_connect = true;
					requestData.is_onprem_setup = true;
					requestData.cloud_url = `${cloudUrl.replace(/\/+$/, "")}/api/v1/`;
					requestData.cloud_sync_enabled = true;

					break;

				case CONFIG_MODE_E.GATE_GUARD_OFFLINE:
					requestData.mode = CONFIG_MODE_E.GATE_GUARD;
					requestData.enable_gg_connect = false;
					requestData.is_onprem_setup = false;
					requestData.cloud_url = "";
					requestData.cloud_sync_enabled = false;

					break;

				default:
					requestData.mode = location.state.mode as CONFIG_MODE_E;
					requestData.enable_gg_connect = true;
					requestData.is_onprem_setup = false;
					requestData.cloud_url = "https://connect.stellarview.ai/api/v1/";
					requestData.cloud_sync_enabled = true;

					break;
			}
		} else if (siteConfig && typeof siteConfig.mode === "number") {
			requestData.mode = siteConfig.mode as CONFIG_MODE_E;
		}

		updateConfig(requestData)
			.unwrap()
			.then(() => {
				updateSyncController()
					.unwrap()
					.then(() => {
						if (requestData.enable_gg_connect) {
							handleStartOTSSync();
						} else {
							handleProceedManualRegistration();
						}
					})
					.catch((error) => {
						dispatch(
							showFeedbackNotification({
								message: "data" in error && error.data ? String(error.data) : "Something went wrong",
								severity: "error"
							})
						);
					})
					.finally(() => {
						resetUpdateConfigState();
						resetUpdateSyncControllerState();
					});
			})
			.catch((error) => {
				dispatch(
					showFeedbackNotification({
						message: "data" in error && error.data ? String(error.data) : "Something went wrong",
						severity: "error"
					})
				);

				resetUpdateConfigState();
				resetUpdateSyncControllerState();
			});
	}

	function handleSyncCompleted() {
		restartBackgroundJobs()
			.unwrap()
			.then(() => {
				navigate("/ots/add-camera", { state: { ...location.state } });
			})
			.catch((error) => {
				dispatch(
					showFeedbackNotification({
						message: "data" in error && error.data ? String(error.data) : "Something went wrong",
						severity: "error"
					})
				);
			});
	}

	function handleBackButtonClick() {
		navigate(-1);
	}

	// function handleBackButtonClick() {
	// 	resetStartOTSSync();
	// }

	useEffect(() => {
		if (siteConfig) {
			const connectIp = (siteConfig.cloud_url ?? "").replace("/api/v1", "");

			reset({
				serial_number: siteConfig.device_uuid,
				local_ip: siteConfig.local_ipv4_address,
				global_ip: siteConfig.global_ipv4_address,
				device_type: String(siteConfig.barrier_config),
				connect_ip: connectIp
			});
		} else {
			reset({
				serial_number: "",
				local_ip: "",
				global_ip: "",
				device_type: "",
				connect_ip: ""
			});
		}
	}, [reset, siteConfig]);

	useEffect(() => {
		if (startSyncSuccess && startSyncResponse && "success" in startSyncResponse && startSyncResponse.success) {
			startGetSyncStatus();
			setStatusPollingEnabled(true);
		}
	}, [startGetSyncStatus, startSyncResponse, startSyncSuccess]);

	useEffect(() => {
		if (getSyncStatusResponse && (getSyncStatusResponse.is_synced || getSyncStatusResponse.is_failed)) {
			setStatusPollingEnabled(false);
		}
	}, [getSyncStatusSuccess, getSyncStatusResponse]);

	function getErrorMessage(): string {
		if (startSyncSuccess) {
			if (startSyncResponse && "subscription" in startSyncResponse && !startSyncResponse.subscription) {
				return startSyncResponse.detail;
			}

			if (getSyncStatusResponse && getSyncStatusResponse.is_failed) {
				return "Device failed to automatically synchronize with Gate-Guard cloud platform. Please retry synchronizing with cloud or register the device manually without Gate-Guard cloud.";
			}
		} else {
			if (startSyncError && "data" in startSyncError) {
				if (startSyncError.data) return String(startSyncError.data);
			}
		}

		return "Something went wrong";
	}

	const StatusRenderer = {
		loading: (
			<Box className="ots-screen-content-wrapper">
				<Box className="ots-sync-status-details-wrapper">
					<Grid container spacing={2} alignItems="center" sx={{ height: "80%" }}>
						<Grid item xs={12} md={7}>
							<Typography variant="h4" fontWeight={600} color="var(--color-primary-dark)" marginBottom="1.25rem">
								Synchronizing with Gate-Guard Connect platform
							</Typography>

							<Typography variant="h6" color="var(--color-primary-main)" marginBottom="0.75rem">
								{`Serial number: ${serialNumber}`}
							</Typography>

							<Typography color="#00000099">
								The device is synchronizing property data with Gate-Guard Connect platform. This process will take up to
								3 minutes.
							</Typography>
						</Grid>

						<Grid item xs={12} md={5}>
							<Box className="ots-sync-loader-wrapper">
								<Lottie
									width="80%"
									options={{
										loop: true,
										autoplay: true,
										animationData: SyncLoaderJSON,
										rendererSettings: { preserveAspectRatio: "xMidYMid slice" }
									}}
								/>
							</Box>
						</Grid>
					</Grid>
				</Box>

				{/* <Box className="ots-form-actions-wrapper">
					<Button variant="outlined" color="error" sx={{ paddingX: 5 }}>
						Cancel
					</Button>

					<LoadingButton
						disableElevation
						variant="contained"
						color="primary"
						loading={manualRegistrationLoader}
						onClick={handleOpenManualRegistrationWarningDialog}
					>
						Register Manually
					</LoadingButton>
				</Box> */}
			</Box>
		),
		error: (
			<Box className="ots-screen-content-wrapper">
				<Grid container spacing={2} alignItems="center" sx={{ height: "80%" }}>
					<Grid item xs={12} md={7}>
						<Typography variant="h4" fontWeight={600} color="var(--color-primary-dark)" marginBottom="1.25rem">
							Gate-Guard Connect Sync Failed!
						</Typography>

						<Typography variant="h6" color="var(--color-primary-main)" marginBottom="0.75rem">
							{`Serial number: ${serialNumber}`}
						</Typography>

						<Typography color="#00000099" marginBottom="2rem">
							{/* {startSyncSuccess
								? getSyncStatusResponse && getSyncStatusResponse.is_failed
									? "Device failed to automatically synchronize with Gate-Guard cloud platform. Please retry synchronizing with cloud or register the device manually without Gate-Guard cloud."
									: "Something went wrong"
								: startSyncError && "data" in startSyncError
								? startSyncError.data
									? String(startSyncError.data)
									: "Something went wrong"
								: startSyncResponse && "subscription" in startSyncResponse
								? startSyncResponse.detail
								: "Something went wrong"} */}
							{getErrorMessage()}
						</Typography>

						<Box className="ots-sync-failed-actions-wrapper">
							<Button disableElevation variant="contained" color="primary" onClick={handleStartOTSSync}>
								Retry synchronizing with Gate-Guard Connect
							</Button>

							{/* <LoadingButton
								color="primary"
								loading={manualRegistrationLoader}
								onClick={handleOpenManualRegistrationWarningDialog}
							>
								Register Manually
							</LoadingButton> */}
						</Box>
					</Grid>

					<Grid item xs={12} md={5}>
						<SyncFailedImage width="90%" />
					</Grid>
				</Grid>
			</Box>
		),
		success: (
			<Box className="ots-screen-content-wrapper">
				<Box className="ots-sync-status-details-wrapper">
					<Grid container spacing={2} alignItems="center" sx={{ height: "80%" }}>
						<Grid item xs={12} md={7}>
							<Typography variant="h4" fontWeight={600} color="var(--color-primary-dark)" marginBottom="1.25rem">
								Synchronization with Gate-Guard Connect complete
							</Typography>

							<Typography variant="h6" color="var(--color-primary-main)" marginBottom="0.75rem">
								{`Serial number: ${serialNumber}`}
							</Typography>

							<Typography color="#00000099">
								The device has finished importing property data from Gate-Guard Connect platform. Please proceed and
								finish the camera setup.
							</Typography>
						</Grid>

						<Grid item xs={12} md={5}>
							<SyncSuccessImage width="90%" />
						</Grid>
					</Grid>
				</Box>

				<Box className="ots-form-actions-wrapper">
					{/* <Button variant="outlined" color="error" sx={{ paddingX: 5 }} onClick={handleBackButtonClick}>
						Back
					</Button> */}

					<LoadingButton
						disableElevation
						variant="contained"
						color="primary"
						onClick={handleSyncCompleted}
						loading={restartBackgroundJobsLoading}
					>
						Save and Next
					</LoadingButton>
				</Box>
			</Box>
		),
		form: (
			<Box className="ots-screen-content-wrapper" component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
				<Box className="ots-form-wrapper">
					<Grid container rowSpacing={4} columnSpacing={6}>
						<Grid item xs={12} md={6}>
							<InputLabel
								required
								htmlFor="serial-number-input"
								error={!!errors.serial_number}
								classes={{ root: "ots-input-label-root" }}
							>
								Serial Number
							</InputLabel>

							<TextField
								required
								fullWidth
								variant="filled"
								color="primary"
								id="serial-number-input"
								placeholder="Enter the Serial Number"
								autoComplete="off"
								error={!!errors.serial_number}
								helperText={
									errors.serial_number && errors.serial_number.message ? errors.serial_number.message.toString() : ""
								}
								InputProps={{
									hiddenLabel: true,
									disableUnderline: true,
									classes: { root: "ots-input-root" },
									readOnly: true
									// readOnly: siteConfig && !!siteConfig.device_uuid
								}}
								{...register("serial_number", { required: "Serial Number is required" })}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<InputLabel
								required
								htmlFor="local-ip-input"
								error={!!errors.local_ip}
								classes={{ root: "ots-input-label-root" }}
							>
								Local IP
							</InputLabel>

							<TextField
								required
								fullWidth
								variant="filled"
								color="primary"
								id="local-ip-input"
								placeholder="Enter Local IP"
								autoComplete="off"
								error={!!errors.local_ip}
								helperText={errors.local_ip && errors.local_ip.message ? errors.local_ip.message.toString() : ""}
								InputProps={{
									hiddenLabel: true,
									disableUnderline: true,
									classes: { root: "ots-input-root" },
									readOnly: true
									// readOnly: siteConfig && !!siteConfig.local_ipv4_address
								}}
								{...register("local_ip", {
									required: "Local IP is required",
									pattern: { value: IP_V4_REGEX, message: "Enter a valid IPv4 address" }
								})}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<InputLabel
								required
								htmlFor="global-ip-input"
								error={!!errors.global_ip}
								classes={{ root: "ots-input-label-root" }}
							>
								Global IP
							</InputLabel>

							<TextField
								required
								fullWidth
								variant="filled"
								color="primary"
								id="global-ip-input"
								placeholder="Enter the Global IP"
								autoComplete="off"
								error={!!errors.global_ip}
								helperText={errors.global_ip && errors.global_ip.message ? errors.global_ip.message.toString() : ""}
								InputProps={{
									hiddenLabel: true,
									disableUnderline: true,
									classes: { root: "ots-input-root" },
									readOnly: true
									// readOnly: siteConfig && !!siteConfig.global_ipv4_address
								}}
								{...register("global_ip", {
									required: "Global IP is required",
									pattern: { value: IP_V4_REGEX, message: "Enter a valid IPv4 address" }
								})}
							/>
						</Grid>

						{location.state &&
						typeof location.state.mode === "number" &&
						location.state.mode === CONFIG_MODE_E.GATE_GUARD_ON_PREMISE ? (
							<Grid item xs={12} md={6}>
								<InputLabel
									required
									htmlFor="connect-ip-input"
									error={!!errors.connect_ip}
									classes={{ root: "ots-input-label-root" }}
								>
									Connect Device IP
								</InputLabel>

								<TextField
									required
									fullWidth
									variant="filled"
									color="primary"
									id="connect-ip-input"
									placeholder="Enter the GateGuard Connect device IP"
									autoComplete="off"
									error={!!errors.connect_ip}
									helperText={
										errors.connect_ip && errors.connect_ip.message ? errors.connect_ip.message.toString() : ""
									}
									InputProps={{
										hiddenLabel: true,
										disableUnderline: true,
										classes: { root: "ots-input-root" }
										// readOnly: true
									}}
									{...register("connect_ip", {
										required: "Connect IP is required"
										// pattern: { value: IP_V4_REGEX, message: "Enter a valid IPv4 address" }
									})}
								/>
							</Grid>
						) : null}

						{/* <Grid item xs={12} md={6}>
							<InputLabel
								required
								htmlFor="unique-identifier-input"
								error={!!errors.unique_identifier}
								classes={{ root: "ots-input-label-root" }}
							>
								Unique Identifier
							</InputLabel>

							<TextField
								required
								fullWidth
								variant="filled"
								color="primary"
								id="unique-identifier-input"
								placeholder="Enter a Unique identifier"
								error={!!errors.unique_identifier}
								helperText={
									errors.unique_identifier && errors.unique_identifier.message
										? errors.unique_identifier.message.toString()
										: ""
								}
								InputProps={{
									hiddenLabel: true,
									disableUnderline: true,
									classes: { root: "ots-input-root" }
								}}
								{...register("unique_identifier", { required: "Unique Identifier is required" })}
							/>
						</Grid> */}

						<Grid item xs={12}>
							<Typography variant="h5" color="var(--color-primary-dark)" fontWeight={500}>
								Device Type
							</Typography>

							<Divider
								sx={{
									borderColor: "var(--color-primary-dark)",
									maxWidth: "300px",
									marginTop: "0.5rem",
									marginBottom: "1rem"
								}}
							/>

							<FormControl fullWidth>
								<RadioGroup row sx={{ gap: "2rem" }}>
									<FormControlLabel
										value={BARRIER_CONFIG_E.SINGLE_ENTRY}
										control={<Radio />}
										label="Single (Entry)"
										checked={Number(deviceType) === BARRIER_CONFIG_E.SINGLE_ENTRY}
										{...register("device_type", { required: "Device Type is required" })}
									/>

									<FormControlLabel
										value={BARRIER_CONFIG_E.SINGLE_EXIT}
										control={<Radio />}
										label="Single (Exit)"
										checked={Number(deviceType) === BARRIER_CONFIG_E.SINGLE_EXIT}
										{...register("device_type", { required: "Device Type is required" })}
									/>

									{siteConfig && siteConfig.device_type !== RPI_DEVICE_NAME ? (
										<FormControlLabel
											value={BARRIER_CONFIG_E.DOUBLE}
											control={<Radio />}
											label="Both Entry and Exit"
											checked={Number(deviceType) === BARRIER_CONFIG_E.DOUBLE}
											{...register("device_type", { required: "Device Type is required" })}
										/>
									) : null}
								</RadioGroup>

								{errors.device_type && errors.device_type.message ? (
									<FormHelperText error>{errors.device_type.message.toString()}</FormHelperText>
								) : null}
							</FormControl>
						</Grid>
					</Grid>
				</Box>

				<Box className="ots-form-actions-wrapper">
					<Button variant="outlined" color="error" sx={{ paddingX: 5 }} onClick={handleBackButtonClick}>
						Back
					</Button>

					<LoadingButton
						disableElevation
						variant="contained"
						color="primary"
						type="submit"
						loading={updateConfigLoading || updateSyncControllerLoading}
					>
						Save and Next
					</LoadingButton>
				</Box>
			</Box>
		)
	};

	return (
		<>
			{StatusRenderer[status]}

			<ConfirmationDialog
				open={showManualRegistrationWarning}
				title="Manual Registration"
				heading="Are you sure you want to register manually?"
				onClose={handleCloseManualRegistrationWarningDialog}
				onConfirm={handleProceedManualRegistration}
				color="primary"
				warning="If you decide to integrate the device with Gate-Guard Connect in the future, you will lose all the existing data on the device."
				warningColor="var(--color-error-main)"
				width="sm"
			/>
		</>
	);
};

export default DeviceRegistration;
